export class ReportingLocalStorageUtil {
  private static readonly ACCOUNT_UUID_KEY = 'reporting_account_uuid';
  private static readonly ENTITY_KEY_PREFIX = 'reporting_selected_entities_';
  private static readonly TEAM_FILTER_KEY_PREFIX = 'reporting_teams_filter_';
  private static readonly ASSIGNMENT_TAB_KEY = 'reporting_assignment_tab';
  private static readonly PRACTICE_FILTER_KEY = 'reporting_practice_filter';
  private static readonly CERTIFICATION_FILTER_KEY = 'reporting_certification_filter';

  static getStoredAccountUuid(): string | null {
    return localStorage.getItem(this.ACCOUNT_UUID_KEY);
  }

  static setStoredAccountUuid(accountUuid: string): void {
    localStorage.setItem(this.ACCOUNT_UUID_KEY, accountUuid);
  }

  private static buildStorageKey(accountUuid?: string, userUuid?: string, assignmentUuid?: string, entityKeyPrefix?: string): string {
    const prefix = entityKeyPrefix ?? this.ENTITY_KEY_PREFIX;
    const parts = [accountUuid, userUuid, assignmentUuid].filter(Boolean);
    if (parts.length) {
        return `${prefix}${parts.join('_')}`;
    }
    return prefix;
  }

  static getStoredSelectedEntities(
    accountUuid?: string,
    userUuid?: string,
    assignmentUuid?: string,
    entityKeyPrefix?: string
  ): string[] | string | null {
    const key = this.buildStorageKey(accountUuid, userUuid, assignmentUuid, entityKeyPrefix);
    const raw = localStorage.getItem(key);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  static setStoredSelectedEntities(
    selectedEntities: string[] | string,
    accountUuid?: string,
    userUuid?: string,
    assignmentUuid?: string,
    entityKeyPrefix?: string
  ): void {
    const key = this.buildStorageKey(accountUuid, userUuid, assignmentUuid, entityKeyPrefix);
    localStorage.setItem(key, JSON.stringify(selectedEntities));
  }

  static getStoredTeamsFilter(accountUuid?: string): string[] | null {
    const storedTeams = this.getStoredSelectedEntities(
      accountUuid,
      undefined,
      undefined,
      this.TEAM_FILTER_KEY_PREFIX
    );

    return Array.isArray(storedTeams) ? storedTeams : null;
  }

  static setStoredTeamsFilter(selectedTeams: string[], accountUuid?: string): void {
    this.setStoredSelectedEntities(
      selectedTeams,
      accountUuid,
      undefined,
      undefined,
      this.TEAM_FILTER_KEY_PREFIX
    );
  }

  // Assignment tab methods

  static getStoredAssignmentTab(): string | null {
    return localStorage.getItem(this.ASSIGNMENT_TAB_KEY);
  }

  static setStoredAssignmentTab(tab: string): void {
    console.log('Setting stored assignment tab to:', tab);
    localStorage.setItem(this.ASSIGNMENT_TAB_KEY, tab);
  }

  // Assignment filter methods

  static getStoredPracticeAssignmentFilter(): string | null {
    return localStorage.getItem(this.PRACTICE_FILTER_KEY);
  }

  static setStoredPracticeAssignmentFilter(filter: string): void {
    localStorage.setItem(this.PRACTICE_FILTER_KEY, filter);
  }

  static getStoredCertificationAssignmentFilter(): string | null {
    return localStorage.getItem(this.CERTIFICATION_FILTER_KEY);
  }

  static setStoredCertificationAssignmentFilter(filter: string): void {
    localStorage.setItem(this.CERTIFICATION_FILTER_KEY, filter);
  }

  static clearAllReportingData(): void {
    localStorage.removeItem(this.ACCOUNT_UUID_KEY);
    localStorage.removeItem(this.ASSIGNMENT_TAB_KEY);
    localStorage.removeItem(this.PRACTICE_FILTER_KEY);
    localStorage.removeItem(this.CERTIFICATION_FILTER_KEY);

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.ENTITY_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
      if (key.startsWith(this.TEAM_FILTER_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }

  static clearEntitySelectionsForAccount(accountUuid: string): void {
    localStorage.removeItem(`${this.ENTITY_KEY_PREFIX}${accountUuid}`);
  }

  static clearTeamsFilterForAccount(accountUuid: string): void {
    localStorage.removeItem(`${this.TEAM_FILTER_KEY_PREFIX}${accountUuid}`);
  }

  static clearAllTeamsFilters(): void {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(this.TEAM_FILTER_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }
}
